import React from 'react';
import { theme } from '../styles/theme';
import Container from '../components/ui/Container';
import FooterPageContent from '../components/layout/FooterPageContent';

const ReturnsPage = () => {
  const pageStyles = {
    padding: `${theme.spacing.xxl} 0`,
    backgroundColor: theme.colors.background,
    minHeight: '100vh',
  };

  const contentStyles = {
    maxWidth: '900px',
    margin: '0 auto',
    padding: `0 ${theme.spacing.lg}`,
  };

  const headingStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.xxl,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.primary,
    marginBottom: theme.spacing.lg,
    textAlign: 'center',
  };

  const subHeadingStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.primaryDark,
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.md,
  };

  const sectionHeadingStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.primary,
    marginTop: theme.spacing.lg,
    marginBottom: theme.spacing.sm,
  };

  const paragraphStyles = {
    fontSize: theme.typography.fontSize.base,
    lineHeight: theme.typography.lineHeight.relaxed,
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
  };

  const listStyles = {
    fontSize: theme.typography.fontSize.base,
    lineHeight: theme.typography.lineHeight.relaxed,
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
    paddingLeft: theme.spacing.xl,
  };

  const contactBoxStyles = {
    backgroundColor: theme.colors.neutral,
    padding: theme.spacing.lg,
    borderRadius: theme.borderRadius.md,
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.lg,
  };

  const highlightBoxStyles = {
    backgroundColor: theme.colors.primaryLight,
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.lg,
    borderLeft: `4px solid ${theme.colors.primary}`,
  };

  const warningBoxStyles = {
    backgroundColor: '#fff3cd',
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.lg,
    borderLeft: `4px solid #ffc107`,
  };

  const lastUpdatedStyles = {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: theme.spacing.xl,
    fontStyle: 'italic',
  };

  return (
    <FooterPageContent slug="returns">
    <div style={pageStyles}>
      <Container>
        <div style={contentStyles}>
          <h1 style={headingStyles}>Returns & Refunds Policy</h1>
          <p style={lastUpdatedStyles}>Last Updated: January 2026</p>

          <p style={paragraphStyles}>
            At BS Trading Co., we want you to be completely satisfied with your purchase. If you're 
            not happy with your order, we're here to help. Please review our return and refund policy 
            below.
          </p>

          <h2 style={subHeadingStyles}>1. Return Eligibility</h2>
          <p style={paragraphStyles}>
            To be eligible for a return, items must meet the following conditions:
          </p>
          <ul style={listStyles}>
            <li>Return request initiated within 30 days of delivery</li>
            <li>Items must be unused and in original condition</li>
            <li>Original packaging and tags must be intact</li>
            <li>Proof of purchase (receipt or order confirmation) required</li>
          </ul>

          <div style={warningBoxStyles}>
            <p style={{ ...paragraphStyles, marginBottom: 0 }}>
              <strong>Important:</strong> Some items may have different return policies or may not 
              be eligible for return. Please see the "Non-Returnable Items" section below.
            </p>
          </div>

          <h2 style={subHeadingStyles}>2. Non-Returnable Items</h2>
          <p style={paragraphStyles}>
            The following items cannot be returned unless defective or damaged:
          </p>
          <ul style={listStyles}>
            <li>Custom or special-order items</li>
            <li>Personalized products</li>
            <li>Items marked as "Final Sale" or "Clearance"</li>
            <li>Used, worn, or altered items</li>
            <li>Items without original packaging or tags</li>
            <li>Perishable goods (if applicable)</li>
          </ul>

          <h2 style={subHeadingStyles}>3. How to Initiate a Return</h2>
          <p style={paragraphStyles}>
            To start a return, please follow these steps:
          </p>

          <h3 style={sectionHeadingStyles}>Step 1: Contact Us</h3>
          <p style={paragraphStyles}>
            Email us at <a href="mailto:Orders@BSTrading.com" style={{ color: theme.colors.primary }}>Orders@BSTrading.com</a> or 
            call <a href="tel:8174474433" style={{ color: theme.colors.primary }}>817-447-4433</a> with:
          </p>
          <ul style={listStyles}>
            <li>Your order number</li>
            <li>Item(s) you wish to return</li>
            <li>Reason for return</li>
            <li>Photos (if item is damaged or defective)</li>
          </ul>

          <h3 style={sectionHeadingStyles}>Step 2: Receive Return Authorization</h3>
          <p style={paragraphStyles}>
            We'll review your request and provide you with a Return Authorization (RA) number and 
            return shipping instructions. Do not ship items back without an RA number.
          </p>

          <h3 style={sectionHeadingStyles}>Step 3: Ship Your Return</h3>
          <p style={paragraphStyles}>
            Pack items securely in original packaging (if possible) and include:
          </p>
          <ul style={listStyles}>
            <li>RA number clearly marked on the outside of the package</li>
            <li>Copy of your order confirmation or receipt</li>
            <li>All original tags, accessories, and documentation</li>
          </ul>

          <div style={highlightBoxStyles}>
            <p style={{ ...paragraphStyles, marginBottom: 0 }}>
              <strong>Return Shipping Costs:</strong> Customers are responsible for return shipping 
              costs unless the item is defective or we made an error. We recommend using a trackable 
              shipping method and purchasing shipping insurance for valuable items.
            </p>
          </div>

          <h2 style={subHeadingStyles}>4. Refund Process</h2>
          
          <h3 style={sectionHeadingStyles}>Processing Time</h3>
          <p style={paragraphStyles}>
            Once we receive your return, we will:
          </p>
          <ul style={listStyles}>
            <li>Inspect the item(s) within 3-5 business days</li>
            <li>Send you an email confirmation of receipt and approval status</li>
            <li>Process approved refunds within 5-7 business days</li>
          </ul>

          <h3 style={sectionHeadingStyles}>Refund Method</h3>
          <p style={paragraphStyles}>
            Refunds will be issued to the original payment method:
          </p>
          <ul style={listStyles}>
            <li>Credit/Debit cards: 5-10 business days (depending on your bank)</li>
            <li>PayPal: 3-5 business days</li>
            <li>Other payment methods: as applicable</li>
          </ul>

          <h3 style={sectionHeadingStyles}>Partial Refunds</h3>
          <p style={paragraphStyles}>
            Partial refunds may be issued for:
          </p>
          <ul style={listStyles}>
            <li>Items showing obvious signs of use</li>
            <li>Items missing original packaging or tags</li>
            <li>Items returned after 30 days but within 60 days</li>
            <li>Damaged items (not caused by shipping)</li>
          </ul>

          <h2 style={subHeadingStyles}>5. Exchanges</h2>
          <p style={paragraphStyles}>
            We accept exchanges for defective or damaged items only. If you need to exchange an item:
          </p>
          <ul style={listStyles}>
            <li>Contact us immediately upon receiving a defective item</li>
            <li>Provide photos of the defect or damage</li>
            <li>We'll arrange for a replacement to be sent</li>
            <li>Return shipping will be provided for defective items</li>
          </ul>

          <p style={paragraphStyles}>
            For size or color exchanges, we recommend processing a return and placing a new order 
            to ensure availability.
          </p>

          <h2 style={subHeadingStyles}>6. Damaged or Defective Items</h2>
          <p style={paragraphStyles}>
            If you receive a damaged or defective item:
          </p>
          <ul style={listStyles}>
            <li>Contact us within 7 days of delivery</li>
            <li>Provide clear photos of the damage or defect</li>
            <li>Keep all original packaging</li>
            <li>We'll arrange for replacement or full refund</li>
            <li>Return shipping will be prepaid by BS Trading Co.</li>
          </ul>

          <div style={highlightBoxStyles}>
            <p style={{ ...paragraphStyles, marginBottom: 0 }}>
              <strong>Shipping Damage:</strong> If your package arrives visibly damaged, note it 
              with the carrier and take photos before opening. This helps us file claims with the 
              shipping company.
            </p>
          </div>

          <h2 style={subHeadingStyles}>7. Wrong Item Received</h2>
          <p style={paragraphStyles}>
            If we sent you the wrong item:
          </p>
          <ul style={listStyles}>
            <li>Contact us immediately</li>
            <li>We'll send the correct item at no charge</li>
            <li>Provide a prepaid return label for the incorrect item</li>
            <li>No restocking fees will apply</li>
          </ul>

          <h2 style={subHeadingStyles}>8. Restocking Fees</h2>
          <p style={paragraphStyles}>
            A restocking fee may apply in certain situations:
          </p>
          <ul style={listStyles}>
            <li>Large or custom items: up to 20% restocking fee</li>
            <li>Special orders: up to 30% restocking fee</li>
            <li>Opened or used items: case-by-case basis</li>
          </ul>

          <p style={paragraphStyles}>
            No restocking fees apply to defective, damaged, or incorrectly shipped items.
          </p>

          <h2 style={subHeadingStyles}>9. Wholesale & Bulk Orders</h2>
          <p style={paragraphStyles}>
            Returns for wholesale and bulk orders are handled on a case-by-case basis. Please 
            contact us directly to discuss return options for large orders.
          </p>

          <h2 style={subHeadingStyles}>10. Cancellations</h2>
          <p style={paragraphStyles}>
            You may cancel your order before it ships:
          </p>
          <ul style={listStyles}>
            <li>Contact us as soon as possible</li>
            <li>Provide your order number</li>
            <li>If order hasn't shipped, we'll cancel and refund in full</li>
            <li>If order has shipped, standard return policy applies</li>
          </ul>

          <h2 style={subHeadingStyles}>11. Contact Us</h2>
          <p style={paragraphStyles}>
            If you have questions about returns or need assistance, please contact us:
          </p>

          <div style={contactBoxStyles}>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>BS Trading Co.</strong>
            </p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>253 Loy St</p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>Burleson, TX 76028</p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>Phone:</strong> <a href="tel:8174474433" style={{ color: theme.colors.primary }}>817-447-4433</a>
            </p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>Toll-Free:</strong> <a href="tel:18005540910" style={{ color: theme.colors.primary }}>1-800-554-0910</a>
            </p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>Email:</strong> <a href="mailto:Orders@BSTrading.com" style={{ color: theme.colors.primary }}>Orders@BSTrading.com</a>
            </p>
            <p style={{ ...paragraphStyles, marginBottom: 0 }}>
              <strong>Hours:</strong> Monday - Friday, 8:00 AM - 5:00 PM CST
            </p>
          </div>

          <div style={warningBoxStyles}>
            <p style={{ ...paragraphStyles, marginBottom: 0 }}>
              <strong>Note:</strong> This policy applies to purchases made through our website or 
              directly from BS Trading Co. For items purchased through third-party retailers, 
              please refer to their return policies.
            </p>
          </div>
        </div>
      </Container>
    </div>
    </FooterPageContent>
  );
};

export default ReturnsPage;

